<template>
  <LoadingSpinner v-if="projectLoading || workOrdersLoading || purchaseOrdersLoading" />
  <div v-else class="list-expenses">
    <ProjectHeader :project="project" :hide-actions="true" />
    <ProjectSubNavigation v-if="isEmployee" :project="project" />

    <div class="work-orders mt-8 card">
      <div class="flex justify-between items-center">
        <h3 class="text-lg font-semibold">Work Orders</h3>
        <BaseButton
          v-if="isEmployee"
          :to="{ name: 'CreateWorkOrder', query: { projectId, backTo: $route.fullPath } }"
          variant="base"
          icon-class="i-heroicons-plus"
        >
          Create Work Order
        </BaseButton>
      </div>

      <BaseTable
        v-if="workOrders"
        :is-pending="workOrdersLoading"
        :items="workOrders"
        :fields="workOrderFields"
        :hide-header="false"
      >
        <template #workOrderId-data="{ row }">
          <RouterLink :to="{ name: 'ShowWorkOrder', params: { id: row.id } }">
            <span>{{ row.workOrderId }}</span>
          </RouterLink>
        </template>

        <template #status-data="{ row }">
          <BaseBadge :variant="row.status === WORK_ORDER_STATUS.CLOSED ? 'success' : 'warning'">
            <span class="capitalize">{{ row.status }}</span>
          </BaseBadge>
        </template>

        <template #date-data="{ row }">
          <span>{{ formatDate(row.date) }}</span>
        </template>

        <template #subId-data="{ row }">
          <RouterLink v-if="subcontractors?.find(sub => sub.id === row.subId)?.id" :to="{ name: 'ShowSubcontractor', params: { id: row.subId } }">
            <span>{{ subcontractors?.find(sub => sub.id === row.subId)?.name}}</span>
          </RouterLink>
          <span v-else>
            {{ row.subId }}
          </span>
        </template>

        <template #total-data="{ row }">
          {{ currency(row.total) }}
        </template>
      </BaseTable>
    </div>

    <div class="purchase-orders mt-8 card">
      <div class="flex justify-between items-center">
        <h3 class="text-lg font-semibold">Purchase Orders</h3>
        <BaseButton
          v-if="isEmployee"
          :to="{ name: 'CreatePurchaseOrder', query: { projectId, backTo: $route.fullPath } }"
          variant="base"
          icon-class="i-heroicons-plus"
        >
          Create Purchase Order
        </BaseButton>
      </div>

      <BaseTable
        v-if="purchaseOrders"
        :is-pending="purchaseOrdersLoading"
        :items="purchaseOrders"
        :fields="purchaseOrderFields"
        :hide-header="false"
      >
        <template #purchaseOrderId-data="{ row }">
          <router-link :to="{ name: 'ShowPurchaseOrder', params: { id: row.id } }">{{ row.purchaseOrderId }}</router-link>
        </template>

        <template #status-data="{ row }">
          <BaseBadge
            :variant="row.status === PURCHASE_ORDER_STATUS.CLOSED ? 'success' : 'warning'"
          >
            <span class="capitalize">{{ row.status }}</span>
          </BaseBadge>
        </template>

        <template #date-data="{ row }">
          <span>{{ formatDate(row.date) }}</span>
        </template>

        <template #vendorId-data="{ row }">
          <router-link v-if="vendors?.find(vendor => vendor.id === row.vendorId)?.id" :to="{ name: 'ShowVendor', params: { id: row.vendorId } }">
            {{ vendors?.find(vendor => vendor.id === row.vendorId)?.name || row.vendorId }}
          </router-link>
          <span v-else>
            {{ row.vendorId }}
          </span>
        </template>

        <template #total-data="{ row }">
          {{ currency(row.total) }}
        </template>
      </BaseTable>
    </div>
</div>
</template>

<script lang="ts" setup>
import ProjectSubNavigation from '@/components/project/ProjectSubNavigation.vue'
import ProjectHeader from '@/components/project/ProjectHeader.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import ProjectService from '@/services/ProjectService'
import PurchaseOrderService from '@/services/PurchaseOrderService'
import WorkOrderService from '@/services/WorkOrderService'
import { useQuery } from '@tanstack/vue-query'
import { useAuth } from '@/composables/useAuth'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { formatDate, currency } from '@/utils'
import useGetAllSubs from '@/composables/useGetAllSubs'
import useGetAllvendors from '@/composables/useGetAllVendors'
import { WORK_ORDER_STATUS } from '@/models/workOrder'
import { PURCHASE_ORDER_STATUS } from '@/models/purchaseOrder'

useHead({ title: 'Project Expenses' })

const $route = useRoute()
const { isEmployee } = useAuth()
const projectId = $route.params.projectId as string
const workOrderFields = [
  { key: 'workOrderId', label: 'WO#' },
  { key: 'status', label: 'Status' },
  { key: 'date', label: 'Date' },
  { key: 'subId', label: 'Contractor' },
  { key: 'total', label: 'Total' },
]
const purchaseOrderFields = [
  { key: 'purchaseOrderId', label: 'PO#' },
  { key: 'status', label: 'Status' },
  { key: 'date', label: 'Date' },
  { key: 'vendorId', label: 'Vendor' },
  { key: 'total', label: 'Total' },
]

const projectService = new ProjectService()
const purchaseOrderService = new PurchaseOrderService(`budgets/${projectId}`)
const workOrderService = new WorkOrderService(`budgets/${projectId}`)

const { data: project, isLoading: projectLoading } = useQuery({
  queryKey: ['project', projectId],
  queryFn: () => projectService.find(projectId),
  enabled: !!projectId
})

const { data: workOrders, isLoading: workOrdersLoading } = useQuery({
  queryKey: ['workOrders', `budgets/${projectId}/workOrders`],
  queryFn: () => workOrderService.all(),
  enabled: !!projectId
})
const { data: purchaseOrders, isLoading: purchaseOrdersLoading } = useQuery({
  queryKey: ['purchaseOrders', `budgets/${projectId}/purchaseOrders`],
  queryFn: () => purchaseOrderService.all(),
  enabled: !!projectId
})
const { data: subcontractors } = useGetAllSubs()
const { data: vendors  } = useGetAllvendors()
</script>
